<template>
  <component
    :is="tag"
    :id="id"
    ref="root"
    class="list sortable-list"
    :style="{ '--gap': gap }"
    :class="{ '-drop-hovering': droppableItem || data.dragging && isDropOperation && !draggedOutside }"
    @pointerdown="handlePointerDown($event)"
    @pointermove="handleDropPointerMove($event)"
  >
    <template v-if="itemsAsArray.length == 0">
      <slot name="placeholder"></slot>
    </template>
    <div class="sortable-list__gap-helper" aria-hidden="true" ref="gapHelperElement"></div>

    <transition-group-list
      :moveable-dimension="moveableDimension"
      :active="transitionActive"
      :transition-name="transitionName"
    >
      <div
        v-for="(item, index) in orderedItems"
        :key="item[itemKey]"
        :ref="
          (el) => {
            if (el) setItemRef(el as HTMLElement, item[itemKey]);
          }
        "
        :class="[
          lineClass,
          {
            '-selected': selected.includes(item[itemKey] as K),
            'sortable-list__entry': sortable,
            'is-beeing-dragged': isBeingDragged(item[itemKey] as K | symbol),
            '-outside': draggedOutside && isBeingDragged(item[itemKey] as K | symbol),
            '-drag-placeholder': (item[itemKey] as K | symbol) == dropPlaceholderSymbol,
          }
        ]"
        :data-key="item[itemKey]"
        :style="
          (item[itemKey] as K | symbol) == dropPlaceholderSymbol
            ? {
                height: droppableItemSize.height,
                width: droppableItemSize.width
              }
            : {}
        "
        @pointerdown="handleItemPointerDown($event, item[itemKey] as K | symbol)"
      >
        <template v-if="(item[itemKey] as K | symbol) != dropPlaceholderSymbol">
          <slot name="before" :item="item" :index="index" ></slot>
          <slot :item="item" :index="index" :ordered-items="orderedItems">
            {{ item.title }}
          </slot>
          <slot name="after" :item="item" :index="index" ></slot>
        </template>
      </div>

      <slot
        v-if="$slots['after-list']"
        name="after-list"
        :length="orderedItems.length"
      ></slot>
    </transition-group-list>

    <teleport v-if="sortable" to="#app">
      <transition
        v-if="sortable"
        name="sortable-list__drag-ghost"
        :appear="true"
        @after-leave="afterDrag"
      >
        <div
          v-show="data.ghostVisible"
          ref="dragGhost"
          :id="`list-${id}-ghost`"
          key="sl-ghost"
          class="sortable-list__drag-ghost -is-ghost"
          :class="ghostListClass"
          :style="{
            transform: `translateY(${
              data.ghostScreenPosition.y
            }px) translateX(${data.ghostScreenPosition.x}px)`,
            '--gap': gap
          }"
        ></div>
      </transition>
    </teleport>
  </component>
</template>

<script setup lang="ts" generic="Group extends { id: string }, K extends SortableItem[IK], SortableItem extends { [P in IK]: string | number | symbol } & { [P in OK]?: number } & { title?: string }, OK extends string = 'order', IK extends string = 'id'">
import {
  computed,
  watch,
  reactive,
  nextTick,
  onBeforeUpdate,
  useTemplateRef,
  ref,
  type Ref,
  useId
} from "vue";
import {
  filter,
  orderBy,
  mapValues
} from "lodash-es";

import { findCommonAncestor, getElementOrAncestorWithClass } from "@/helpers/utility/DOM";
import TransitionGroupList from "../wrappers/TransitionGroupList.vue";
import { useSortableListDrag } from "./sortableListDrag";
import { notNull } from "@/helpers/utility/Type";
import type { Handleable } from "@core/types/Events";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// type SortableItem = any;

type MoveListEvent = {
  items: SortableItem[];
  targetIndex: number;
  targetList: Group;
  sourceList: Group;
};

const {
  items,
  order = null,
  orderBy: orderByKey = "order" as OK,
  group,
  itemKey = "id" as IK,
  lineClass = "",
  ghostListClass = "",
  sortable = true,
  direction = "vertical",
  tag = "div",
  transitionActive = true,
  transitionName = "transition-list-flex",
  dragThreshold = 10,
  selected = [],
  gap = "0.5rem",
} = defineProps<{
    items: {
      [key: string]: SortableItem;
    } | {
      [key: number]: SortableItem;
    };
    order?: K[] | null;
    selected?: K[];
    orderBy?: OK;
    itemKey?: IK;
    lineClass?: string | string[] | Record<string, boolean>;
    ghostListClass?: string | string[] | Record<string, boolean>;
    sortable?: boolean;
    direction?: "horizontal" | "vertical";
    tag?: string;
    group?: Group;
    transitionActive?: boolean;
    transitionName?: string;
    dragThreshold?: number;
    multiSelect?: boolean;
    gap?: string;
  }>();

type OrderItemPayload = { [P in IK]: K } & { [P in OK]: number };

const emit = defineEmits<{
  (
    e: "reorder-item",
    item: OrderItemPayload
  ): void;
  (e: "update:order", order: SortableItem[IK][]): void;
  (e: "move-list", data: MoveListEvent): void;
}>();

const dropPlaceholderSymbol = Symbol("dropPlaceholder");
const id = useId();
const rootElement = useTemplateRef<HTMLElement>("root");
const dragGhostElement = useTemplateRef<HTMLElement>("dragGhost");
const gapHelperElement = useTemplateRef<HTMLElement>("gapHelperElement");
const sortableListDrag = useSortableListDrag();

const cachedListDimensions = ref<DOMRect | null>(null);
function getListDimensions(): DOMRect {
  if (!rootElement.value) return { x: 0, y: 0, width: 0, height: 0, top: 0, right: 0, bottom: 0, left: 0 } as DOMRect;
  if (cachedListDimensions.value) return cachedListDimensions.value;
  return updateListDimensions();
}
function updateListDimensions(): DOMRect {
  if (!rootElement.value) return { x: 0, y: 0, width: 0, height: 0, top: 0, right: 0, bottom: 0, left: 0 } as DOMRect;
  const dimensions = rootElement.value.getBoundingClientRect();
  cachedListDimensions.value = dimensions;
  return dimensions;
}

// Native drag and drop state
let dragStartPosition: { x: number; y: number } | null = null;
let isDragging = false;

const data = reactive({
  /**
   * [orderposition]: itemKey
   */
  orderedItemKeys: {} as { [index: number]: K | typeof dropPlaceholderSymbol },

  /**
   * Reverse of orderedItemKeys.
   * [itemKey]: orderposition
   */
  indexByKey: {} as { [itemKey: string | symbol]: number },

  elementSizes: {} as {
    [index: number]: { start: number; end: number; size: number };
  },

  gapSize: 8,

  /**
   * The position of the "ghost" of the dragged item.
   */
  ghostPosition: { y: 0, x: 0 },
  ghostScreenPosition: { y: 0, x: 0 },

  skipTransition: false,
  preventClick: false,
  itemRefs: {} as { [id: string | symbol]: HTMLElement },

  dragging: false,
  ghostVisible: true,

  maxEndPos: -1,
  minStartPos: Infinity,
});

/**
 * The main object defining the order of the items.
 */
const orderedItems = computed((): SortableItem[] => {
  if (order == null && sortable == false) return Object.values(items);

  const result: SortableItem[] = [];
  for (const pos in data.orderedItemKeys) {
    const id = data.orderedItemKeys[pos] as K | symbol;
    if (id == dropPlaceholderSymbol) {
      result.push(dropPlaceholderItem.value);
      continue;
    }
    const item = itemsByKey.value.get(id as K);
    if (item) {
      result.push(item);
    }
  }
  return result;
});

const itemsByKey = computed(() => {
  const result = new Map<K, SortableItem>();
  for (const item of itemsAsArray.value) {
    const id = item[itemKey] as K;
    result.set(id, item);
  }
  return result;
});

const itemsAsArray = computed(() => {
  return Object.values(items);
});

/**
 * Used to trigger resorts if the order is not set externally
 */
const itemOrderKeys = computed(() => {
  if (order !== null) return;
  return itemsAsArray.value.map((i) => i?.[orderByKey ?? 0]);
});

const moveableDimension = computed(() => {
  return direction === "vertical" ? "y" : "x";
});

const dropPlaceholderItem = computed((): SortableItem => {
  return {
    [itemKey]: dropPlaceholderSymbol
  } as SortableItem;
});

/**
 * The indices of all items in order (sorted)
 */
const orderIndices = computed(() => {
  return Object.keys(data.orderedItemKeys)
    .map((k) => parseInt(k))
    .sort((a, b) => a - b);
});

watch(
  itemOrderKeys,
  () => {
    if (order !== null) return;
    const itemsArray = itemsAsArray.value;

    // rebuild the ordered item keys:
    const newOrderedItemKeys: { [order: number]: K } = {};
    const indexByKey: { [key: string]: number } = {};
    for (const item of itemsArray) {
      let order = (item[orderByKey] as number) ?? 0;
      while (newOrderedItemKeys[order] != null) {
        order++;
      }
      const itemId = item[itemKey] as K;
      newOrderedItemKeys[order] = itemId;
      indexByKey[itemId] = order;
    }

    data.orderedItemKeys = newOrderedItemKeys;
    data.indexByKey = indexByKey;
  },
  {
    immediate: true,
    deep: true
  }
);

watch(
  () => order,
  (order) => {
    if (order === null) return;
    updateOrderedItemKeysFromOrder(order);
  },
  {
    immediate: true,
    deep: true
  }
);

function updateOrderedItemKeysFromOrder(order: K[]) {
  data.orderedItemKeys = order.slice(0);

  const indexByKey: Record<K, number> = {} as Record<K, number>;
  for (let i = 0; i < order.length; i++) {
    indexByKey[order[i]] = i;
  }
  data.indexByKey = indexByKey;
}

onBeforeUpdate(() => {
  data.itemRefs = {};
});

function setItemRef(el: HTMLElement, id: SortableItem[IK] | symbol) {
  data.itemRefs[id] = el;
}

// ========================================
// DRAG HANDLERS

const isDropOperation = ref(false);
const draggedItemKeys = ref<K[]>([]) as Ref<K[]>;

// Native event handlers
function handleItemPointerDown(event: PointerEvent, itemId: K | symbol) {
  if (typeof itemId === "symbol") return;
  if (event.button !== 0) return; // Only left mouse button
  if (!sortable) return;
  if (selected.includes(itemId)) {
    draggedItemKeys.value = [...selected].filter(filterKeyByIndex).sort(sortKeysByIndex);
  } else {
    if (event.metaKey || event.ctrlKey) {
      draggedItemKeys.value = [...selected, itemId].filter(filterKeyByIndex).sort(sortKeysByIndex);
    } else {
      draggedItemKeys.value = [itemId];
    }
  }
}

function handlePointerDown(event: PointerEvent) {
  if (event.button !== 0) return; // Only left mouse button
  if (!sortable) return;

  const target = event.target as HTMLElement;
  if (!target) return;

  // Check if we're clicking on a draggable area
  const sortableEntry = getElementOrAncestorWithClass(target, "sortable-list__entry");
  if (!sortableEntry) return;

  // Check if target is not draggable
  if (getElementOrAncestorWithClass(target, "-not-draggable")) return;

  // Check if it's within this list
  const closestSortableList = getElementOrAncestorWithClass(target, "sortable-list");
  if (closestSortableList !== rootElement.value) return;

  const listPosition = updateListDimensions();
  const mousePosition = { x: event.clientX, y: event.clientY };
  dragStartPosition = { x: mousePosition.x - listPosition.left, y: mousePosition.y - listPosition.top };

  // Do not prevent event default! This prevents text selections and other things.
  // We only prevent the default if a drag is actually started (in handlePointerMove).

  document.addEventListener("pointermove", handlePointerMove);
  document.addEventListener("pointerup", handlePointerUp);
  document.addEventListener("pointercancel", handlePointerUp);
}

function handlePointerMove(event: PointerEvent) {
  if (!dragStartPosition || !sortable) return;

  const listPosition = updateListDimensions();
  const mousePosition = { x: event.clientX, y: event.clientY };
  const currentPosition = { x: mousePosition.x - listPosition.x, y: mousePosition.y - listPosition.y };

  // Restrict movement to the specified axis
  if (moveableDimension.value === "y") {
    currentPosition.x = dragStartPosition.x;
  } else {
    currentPosition.y = dragStartPosition.y;
  }

  const distance = Math.sqrt(
    Math.pow(currentPosition.x - dragStartPosition.x, 2) +
    Math.pow(currentPosition.y - dragStartPosition.y, 2)
  );

  if (distance >= dragThreshold && !isDragging) {
    isDropOperation.value = false;
    startDragging(event, currentPosition);
  }

  if (isDragging) {
    const listDimensions = getListDimensions();
    handleDragMove(event, currentPosition);
    data.ghostScreenPosition = {
      x: listDimensions.x + data.ghostPosition.x,
      y: listDimensions.y + data.ghostPosition.y
    };
  }
}

function handlePointerUp(event: PointerEvent) {
  if (isDragging) {
    endDragging(event);
    event.preventDefault();
    isDragging = false;
  }
  
  // Reset state
  dragStartPosition = null;

  document.removeEventListener("pointermove", handlePointerMove);
  document.removeEventListener("pointerup", handlePointerUp);
  document.removeEventListener("pointercancel", handlePointerUp);
}

let dragSize = { x: 0, y: 0 };

function startDragging(event: PointerEvent, mouseListPosition: { x: number; y: number }) {
  updateElementSizes();
  sortableListDrag.clearList();

  if (draggedItemKeys.value.length > 1) {
    groupSelectedDraggedItems(mouseListPosition);
  }
  
  if (group) {
    const indices = draggedItemKeys.value
      .map((key) => findIndexById(key))
      .filter((i) => i !== -1);
    const firstItemSize = data.elementSizes[indices[0]];
    const lastItemSize = data.elementSizes[indices[indices.length - 1]];
    const draggedItemSize = {
      start: firstItemSize.start,
      size: lastItemSize.end - firstItemSize.start,
      end: lastItemSize.end
    };
    if (!draggedItemSize) return;
    const mouseOffset = {
      x: mouseListPosition.x - draggedItemSize.start,
      y: mouseListPosition.y - draggedItemSize.start
    };
    dragSize = {
      x: draggedItemSize.size,
      y: draggedItemSize.size
    };
    sortableListDrag.startDrag({
      listId: id,
      groupId: group.id,
      items: [...draggedItemKeys.value],
      size: { ...dragSize },
      mouseOffset: mouseOffset,
      sourceIndex: indices[0],
      dragStartPosition: { ...mouseListPosition }
    });
  }

  isDragging = true;
  data.dragging = true;
  data.ghostVisible = true;

  prepareDragGhost();

  event.preventDefault();
}

function groupSelectedDraggedItems(mouseListPosition: { x: number; y: number }) {
  const keys = draggedItemKeys.value.slice();
  if (keys.length === 0) return;

  let targetIndex = getIndexAt(mouseListPosition);

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const index = findIndexById(key);
    if (index === -1) continue;
    if (index < targetIndex) {
      moveToIndex(index, targetIndex);
    } else {
      targetIndex += 1;
      moveToIndex(index, targetIndex);
    }
  }

  // Reposition the drag ghost to have a grab on the items
  const size = getDragItemsSize();
  if (!size) return;
  if (!dragStartPosition) return;

  if (dragStartPosition[moveableDimension.value] < size.start) {
    dragStartPosition[moveableDimension.value] = size.start + 5;
  } else if (dragStartPosition[moveableDimension.value] > size.end) {
    dragStartPosition[moveableDimension.value] = size.end - 5;
  }
}

function handleDragMove(event: PointerEvent, mouseListPosition: { x: number; y: number }) {
  if (!isDragging || !dragStartPosition) return;
  
  // Calculate the movement delta
  let moveDelta: number;
  if (moveableDimension.value === "x") {
    moveDelta = mouseListPosition.x - dragStartPosition.x;
    data.ghostPosition.x += moveDelta;
  } else {
    moveDelta = mouseListPosition.y - dragStartPosition.y;
    data.ghostPosition.y += moveDelta;
  }

  // Update drag start position for next delta calculation
  dragStartPosition = { ...mouseListPosition };

  if (sortableListDrag.dragReceiver.value != null) return;

  // Get current item index and calculate new position
  const currentIndices = draggedItemKeys.value
    .map((key) => findIndexById(key))
    .filter((i) => i !== -1);
  if (currentIndices.length === 0) return;

  const size = getDragItemsSize();
  if (!size) return;

  const indexTo = getNewIndex(currentIndices[0], data.ghostPosition[moveableDimension.value], size);
  if (indexTo === -1) return;

  if (currentIndices.includes(indexTo)) return;
  
  const closestIndex = currentIndices[0] < indexTo ? currentIndices[currentIndices.length - 1] : currentIndices[0];
  const differences = currentIndices.map((i) => i - closestIndex);
  const dir = indexTo > closestIndex ? 1 : -1;

  if (dir < 0) {
    for (const diff of differences) {
      moveToIndex(closestIndex + diff, indexTo + diff);
    }
  } else {
    for (let i = differences.length - 1; i >= 0; i--) {
      const diff = differences[i];
      moveToIndex(closestIndex + diff, indexTo + diff);
    }
  }

  // nextTick(() => {
  //   updateElementSizesInRange(
  //     Math.min(indexTo, closestIndex),
  //     Math.max(indexTo, closestIndex)
  //   );
  // });
}

function getDragItemsSize() {
  const sizes = draggedItemKeys.value
    .map((key) => {
      const index = findIndexById(key);
      if (index === -1) return null;
      return data.elementSizes[index];
    })
    .filter((size) => size != null);
  if (sizes.length === 0) return null;
  let minStart = Infinity;
  let maxEnd = -Infinity;
  for (const size of sizes) {
    minStart = Math.min(minStart, size.start);
    maxEnd = Math.max(maxEnd, size.end);
  }
  return { start: minStart, end: maxEnd, size: maxEnd - minStart };
}

function endDragging(event: PointerEvent) {
  isDragging = false;

  if (sortableListDrag.dragReceiver.value) {
    dropItemsToReceiverList();
  } else {
    for (const key of draggedItemKeys.value) {
      const index = findIndexById(key);
      if (index === -1) continue;
      emit("reorder-item", {
        [itemKey]: key,
        [orderByKey]: index
      } as OrderItemPayload);
    }
    emit("update:order", orderedKeysAsArray());
    nextTick(() => {
      const indices = draggedItemKeys.value
        .map((key) => findIndexById(key))
        .filter((i) => i !== -1);
      const newItemPos = data.elementSizes[indices[0]]?.start || 0;
      const listDimensions = getListDimensions();
      data.ghostPosition[moveableDimension.value] = newItemPos;
      data.ghostScreenPosition = {
        x: listDimensions.x + data.ghostPosition.x,
        y: listDimensions.y + data.ghostPosition.y
      };
    });
  }
  
  data.ghostVisible = false;
}

function afterDrag() {
  data.dragging = false;
  draggedItemKeys.value = [];
  sortableListDrag.endDrag();
}

watch(sortableListDrag.dragReceiver, () => {
  if (sortableListDrag.dragOperation.value == null) return;
  if (sortableListDrag.dragOperation.value.listId !== id) return;
  if (sortableListDrag.dragReceiver.value == null) return;
  isDropOperation.value = true;
});

// ====================================
// DROPPING between lists of same group

const droppableItem = ref<null | SortableItem>();
const droppableItemSize = ref<{ width: string; height: string }>({
  width: "0px",
  height: "0px"
});

const droppingItemKeys = computed(() => {
  if (sortableListDrag.dragReceiver.value?.listId !== id) return [];
  return sortableListDrag.dragOperation.value?.items || [];
});

function handleDropPointerMove(event: PointerEvent & Handleable) {
  if (group?.id == null) return;
  const op = sortableListDrag.dragOperation.value;
  if (op == null) return;
  if (op.groupId !== group.id) return;
  if (event.hasBeenHandled) return;
  if (event.buttons === 0) return; // No button pressed

  const closestListId = getFirstListId(event);

  const receiver = sortableListDrag.dragReceiver.value;
  if (receiver?.listId == id) {
    if (closestListId == null || closestListId !== id) {
      // LEAVE DROP LIST
      console.log("leave list", id);
      sortableListDrag.leaveList(id);
      document.removeEventListener("pointerup", handleDropPointerUp, { capture: true });
      document.removeEventListener("pointercancel", handleDropPointerUp, { capture: true });
      return;
    }

    // MOVE IN DROP LIST
    if (op.listId == id) return; // Do not handle move in own list

    const mouseOffset = op.mouseOffset;
    const listPosition = updateListDimensions();
    const position = { x: event.clientX - listPosition.x - mouseOffset.x, y: event.clientY - listPosition.y - mouseOffset.y };
    const newIndex = updateDropPlaceholderIndex(position);
    sortableListDrag.updateList({
      index: newIndex
    });
  } else {
    // ENTER DROP LIST
    if (closestListId == null) return;
    if (closestListId !== id) return;
    if (closestListId === op.listId) {
      // Reset the drop operation as soon as we enter the original list again
      if (sortableListDrag.dragReceiver.value != null) {
        sortableListDrag.clearList();
        nextTick(() => {
          updateListDimensions();
        });
      }
      return;
    }

    const listDimensions = updateListDimensions();

    const position = {
      x: event.clientX - listDimensions.x,
      y: event.clientY - listDimensions.y
    };

    updateElementSizes();
    const indexAtPos = getIndexAt(position);
    if (indexAtPos === -1) {
      console.warn("position", position, "not in list");
      return;
    }

    addDropPlaceholder(indexAtPos, op.size);

    sortableListDrag.enterList({
      listId: id,
      index: indexAtPos,
      groupData: { ...group }
    });
    const onLeave = (leftListId: string) => {
      if (leftListId === id) {
        removeDropPlaceholder();
        document.removeEventListener("pointerup", handleDropPointerUp, { capture: true });
        document.removeEventListener("pointercancel", handleDropPointerUp, { capture: true });
      }
      sortableListDrag.offLeave(onLeave);
    };
    sortableListDrag.onLeave(onLeave);

    event.hasBeenHandled = true;
    
    document.addEventListener("pointerup", handleDropPointerUp, { capture: true });
    document.addEventListener("pointercancel", handleDropPointerUp, { capture: true });
  }
}

function handleDropPointerUp(event: PointerEvent) {
  document.removeEventListener("pointerup", handleDropPointerUp, { capture: true });
  document.removeEventListener("pointercancel", handleDropPointerUp, { capture: true });

  // Update last position to position drag ghost
  const element = data.itemRefs[dropPlaceholderSymbol];
  if (element) {
    const rect = element.getBoundingClientRect();
    const listRect = getListDimensions();
    sortableListDrag.setEndPosition({
      item: {
        x: rect.left,
        y: rect.top
      },
      list: {
        x: listRect.x,
        y: listRect.y,
        id: id
      }
    });
  }

  removeDropPlaceholder();
  nextTick(() => {
    updateElementSizes();
  });
}

function getFirstListId(event: Event) {
  const path = event.composedPath().reverse() as HTMLElement[];
  for (let i = path.length - 1; i >= 0; i--) {
    const el = path[i];
    if (el.classList && el.classList.contains("is-beeing-dragged")) {
      // cut the path here
      path.splice(i, path.length - i);
    }
  }
  path.reverse();
  for (const el of path) {
    if (el.classList && el.classList.contains("sortable-list")) {
      return el.id;
    }
  }
  return null;
}

/**
 * Drop the dragged items into the receiver list that registered
 * itself as the drop target.
 */
function dropItemsToReceiverList() {
  const receiver = sortableListDrag.dragReceiver.value;
  if (!receiver) return;

  const targetIndex = receiver.index;

  const items = draggedItemKeys.value.map((key) => {
    const item = itemsByKey.value.get(key);
    if (!item) return null;
    return item;
  }).filter(notNull);

  // Dropping works via events.
  emit("move-list", {
    items: items,
    targetIndex: targetIndex,
    targetList: receiver.groupData as Group,
    sourceList: group!
  } satisfies MoveListEvent);

  const endPosition = sortableListDrag.dragEndPosition.value;
  if (!endPosition) {
    afterDrag();
    return;
  }

  nextTick(() => {
    const d = moveableDimension.value;
    const listRect = getListDimensions();
    const listPosition = {
      x: listRect.x,
      y: listRect.y,
      id: id
    };
    const otherListPos = endPosition.list;
    // Add an offset to account for list size shifts when the two lists are under each other:
    const offset = getDropListOffset(listPosition, otherListPos);
    const newItemPos = endPosition.item;
    const nextScreenPosition = {
      x: endPosition.item.x,
      y: endPosition.item.y
    };
    nextScreenPosition[d] += offset;
    data.ghostPosition[d] = newItemPos[d] - listPosition[d] + offset;
    data.ghostScreenPosition = {
      x: nextScreenPosition.x,
      y: nextScreenPosition.y
    };
  });
}

function getDropListOffset(firstList: { x: number; y: number; id: string }, secondList: { x: number; y: number; id: string }) {
  const commonAncestor = findCommonAncestor(firstList.id, secondList.id);
  if (commonAncestor == null) return 0;
  if (commonAncestor.tagName == "BODY") return 0;
  const d = moveableDimension.value;
  const offset = secondList[d] > firstList[d] ? 0 : 0;
  return offset;
}

/**
 * Add a placeholder for the drop item space to the list.
 * The placeholder can be used to reserve space for the
 * item at the theoretical drop position.
 * @param dropItem - data of the dropped item
 * @param dropItemSize - html element of drag ghost
 */
function addDropPlaceholder(index: number, dropItemSize: { x: number, y: number }) {
  droppableItem.value = {} as SortableItem;
  // update the drop placeholder position
  if (moveableDimension.value == "x") {
    droppableItemSize.value = {
      width: dropItemSize.x + "px",
      height: "100%"
    };
  } else {
    droppableItemSize.value = {
      width: "100%",
      height: dropItemSize.y + "px"
    };
  }
  
  // add the placeholder to the local list order
  insertAtIndex(index, dropPlaceholderSymbol);

  // initialize the element size for the placeholder
  const sizeAtIndex = data.elementSizes[index];
  const newStart = sizeAtIndex ? sizeAtIndex.start : data.maxEndPos + data.gapSize;
  const newEnd = newStart + dropItemSize[moveableDimension.value];

  // shift all element sizes after the index
  const keys = Object.keys(data.elementSizes).map((k) => parseInt(k)).sort((a, b) => b - a);
  for (const i of keys) {
    if (i >= index) {
      const size = data.elementSizes[i];
      if (!size) continue;
      const newStartPos = size.start + dropItemSize[moveableDimension.value] + data.gapSize;
      const newEndPos = newStartPos + size.size;
      data.elementSizes[i + 1] = {
        start: newStartPos,
        end: newEndPos,
        size: size.size
      };
      if (newEndPos > data.maxEndPos) {
        data.maxEndPos = newEndPos;
      }
      delete data.elementSizes[i];
    }
  }

  // add the size for the placeholder
  data.elementSizes[index] = {
    start: newStart,
    end: newEnd,
    size: dropItemSize[moveableDimension.value],
  };
  if (newEnd > data.maxEndPos) {
    data.maxEndPos = newEnd;
  }
}

function updateDropPlaceholderIndex(position: { x: number, y: number }) {
  // get x or y, depending on the list direction:
  const dimensionValue = position[moveableDimension.value];

  const index = findIndexById(dropPlaceholderSymbol);
  if (index == -1) return;

  const size = data.elementSizes[index];
  const newIndex = getNewIndex(index, dimensionValue, size);
  if (newIndex == -1) return;

  if (newIndex !== index) {
    moveToIndex(index, newIndex);
  }

  return newIndex;
}

/**
 * Remove the space placeholder for the droppable item.
 */
function removeDropPlaceholder() {
  const index = data.indexByKey[dropPlaceholderSymbol];

  // delete from item keys
  if (order) {
    updateOrderedItemKeysFromOrder(order);
  } else {
    const orderedKeys = { ...data.orderedItemKeys };
    delete orderedKeys[index];
    data.orderedItemKeys = orderedKeys;
  }
  delete data.elementSizes[index];
  delete data.indexByKey[dropPlaceholderSymbol];
  delete data.itemRefs[dropPlaceholderSymbol];

  droppableItem.value = null;
  droppableItemSize.value = { width: "0px", height: "0px" };
}

const draggedOutside = computed(() => {
  return sortableListDrag.dragOperation.value?.listId === id && sortableListDrag.dragReceiver.value != null;
});

// ==============================
// INDEX FUNCTIONS

function orderedKeysAsArray() {
  const keys = Object.values(data.orderedItemKeys).filter(
    (key: K | symbol) => key !== dropPlaceholderSymbol
  ) as K[];
  return keys;
}

function insertAtIndex(index: number, key: K | typeof dropPlaceholderSymbol) {
  if (index < 0) {
    throw new Error(`Invalid index: ${index}`);
  }
  const newIndexByKey: { [itemKey: string | symbol]: number } = {};
  const newOrderItemKeys: { [i: number]: K | typeof dropPlaceholderSymbol } = {};
  const entries = Object.entries(data.orderedItemKeys);
  for (const entry of entries) {
    const [key, value] = entry;
    const oIndex = parseInt(key);
    if (oIndex < index) {
      newOrderItemKeys[oIndex] = value;
      newIndexByKey[value] = oIndex;
    } else {
      newOrderItemKeys[oIndex + 1] = value;
      newIndexByKey[value] = oIndex + 1;
    }
  }
  newOrderItemKeys[index] = key;
  newIndexByKey[key] = index;
  data.orderedItemKeys = newOrderItemKeys;
  data.indexByKey = newIndexByKey;
}

function getNewIndex(currentIndex: number, positionStart: number, positionBefore: { start: number; end: number; size: number }) {
  const newStartPos = positionStart;
  const newEndPos = positionStart + positionBefore.size;

  // if the position is outside of the list,
  // return the nearest extreme index.
  if (newStartPos < data.minStartPos) {
    const minIndex = orderIndices.value[0];
    return minIndex;
  }
  if (newEndPos > data.maxEndPos) {
    const maxIndex = orderIndices.value[orderIndices.value.length - 1];
    return maxIndex;
  }

  const DRAG_PADDING = 10;

  // Find the order-index of the element that is under the dragged element
  // Only check the indices that are in the drag direction.
  let i = orderIndices.value.indexOf(currentIndex);
  if (newStartPos < positionBefore.start) {
    while (i > 0) {
      const index = orderIndices.value[i - 1];
      const elementPosition = data.elementSizes[index];
      if (elementPosition.start + DRAG_PADDING < newStartPos) {
        break;
      }
      i--;
    }
    return orderIndices.value[i];
  } else {
    while (i < orderIndices.value.length - 1) {
      const index = orderIndices.value[i + 1];
      const elementPosition = data.elementSizes[index];
      if (elementPosition.end - DRAG_PADDING > newEndPos) {
        break;
      }
      i++;
    }
    return orderIndices.value[i];
  }
}

function getIndexAt(position: { x: number; y: number }) {
  const pos = position[moveableDimension.value];
  let smallestPos = Infinity;
  let largestPos = -Infinity;
  let smallestIndex = Infinity;
  let largestIndex = -Infinity;
  for (const indexKey in data.elementSizes) {
    const index = parseInt(indexKey);
    const elementPosition = data.elementSizes[index];
    const half = elementPosition.size / 2;
    if (pos >= elementPosition.start && pos <= elementPosition.start + half) {
      return index;
    } else if (pos > elementPosition.start + half && pos <= elementPosition.end) {
      return index + 1;
    }
    smallestPos = Math.min(smallestPos, elementPosition.start);
    largestPos = Math.max(largestPos, elementPosition.end);
    smallestIndex = Math.min(smallestIndex, index);
    largestIndex = Math.max(largestIndex, index);
  }
  if (smallestIndex == Infinity) return 0;
  if (pos < smallestPos) return smallestIndex;
  if (pos > largestPos) return largestIndex + 1;
  return -1;
}

function findIndexById(itemKey: number | string | null | symbol) {
  if (itemKey == null) {
    return -1;
  }
  const key = data.indexByKey[itemKey];
  if (typeof key == "number") return key;
  if (isNaN(parseInt(key))) {
    return -1;
  }
  return parseInt(key);
}

/**
 * put an item in the place of another
 * @param indexFrom position of the source item
 * @param indexTo position of the destination item
 */
function swapIndex(indexFrom: number, indexTo: number) {
  if (indexFrom == null || indexTo == null) {
    throw new Error(`cant swap to undefined index ${indexFrom}, ${indexTo}`);
  }
  if (indexFrom === indexTo) return;
  const iTo = data.orderedItemKeys[indexTo];
  const iFrom = data.orderedItemKeys[indexFrom];
  data.orderedItemKeys[indexFrom] = iTo;
  data.indexByKey[iTo] = indexFrom;
  data.orderedItemKeys[indexTo] = iFrom;
  data.indexByKey[iFrom] = indexTo;
  const sizeTo = data.elementSizes[indexTo];
  const sizeFrom = data.elementSizes[indexFrom];
  data.elementSizes[indexFrom] = sizeTo;
  data.elementSizes[indexTo] = sizeFrom;
}

/**
 * move an item from one place to another, switching with the items between on the way
 * @param indexFrom position of the source item
 * @param indexTo position of the destination item
 */
function moveToIndex(indexFrom: number, indexTo: number) {
  if (indexFrom == indexTo) return;

  let indices = Object.keys(data.orderedItemKeys).map((i) => parseInt(i));

  if (indexFrom < indexTo) {
    indices = filter(indices, (index) => index >= indexFrom);
    indices = orderBy(indices, (index) => index, "desc");
    let i = indexFrom;
    while (i < indexTo) {
      const indexNext = indices.pop();
      if (indexNext == null) break;
      swapIndex(i, indexNext);
      i = indexNext;
    }
  } else {
    indices = filter(indices, (index) => index <= indexFrom);
    indices = orderBy(indices, (index) => index, "asc");
    let i = indexFrom;
    while (i > indexTo) {
      const indexNext = indices.pop();
      if (indexNext == null) break;
      swapIndex(i, indexNext);
      i = indexNext;
    }
  }

  // Update element sizes to reflect the new order
  let lastEnd = 0;
  for (let i = 0; i < orderIndices.value.length; i++) {
    const index = orderIndices.value[i];
    const size = data.elementSizes[index];
    size.start = lastEnd;
    size.end = size.start + size.size;
    lastEnd = size.end + data.gapSize;
  }
}

/**
 * Update the sizes of all the elements of the list
 * Important before drag events. This makes sure that
 * the entry hitboxes are correct.
 */
function updateElementSizes() {
  // ordered by item order:
  let maxEndPos = -1;
  let minStartPos = Infinity;

  data.gapSize = gapHelperElement.value?.offsetHeight ?? 8;

  data.elementSizes = mapValues(data.orderedItemKeys, (itemKey) => {
    const element = data.itemRefs[itemKey];
    if (element == null)
      return {
        start: 0,
        size: 0,
        end: 0
      };

    let start, size;
    if (moveableDimension.value === "y") {
      start = element.offsetTop;
      size = element.offsetHeight;
    } else {
      start = element.offsetLeft;
      size = element.offsetWidth;
    }
    const end = start + size;

    if (end > maxEndPos) {
      maxEndPos = end;
    }
    if (start < minStartPos) {
      minStartPos = start;
    }
    return {
      start,
      size,
      end
    };
  });

  data.maxEndPos = maxEndPos;
  data.minStartPos = minStartPos;
}

// function updateElementSizesInRange(minIndex: number, maxIndex: number) {
//   const orderedItemKeys = pickBy(data.orderedItemKeys, (itemKey, index) => {
//     const indexNum = parseInt(index);
//     return indexNum >= minIndex && indexNum <= maxIndex;
//   });
//   const sizes: {
//     [index: number]: { size: number; start: number; end: number };
//   } = mapValues(orderedItemKeys, (itemKey) => {
//     const element = data.itemRefs[itemKey];

//     let start, size;
//     if (moveableDimension.value === "y") {
//       start = element.offsetTop;
//       size = element.offsetHeight;
//     } else {
//       start = element.offsetLeft;
//       size = element.offsetWidth;
//     }

//     if ((itemKey as K | symbol) == dropPlaceholderSymbol) {
//       const dropElementSize = droppableItemSize.value;
//       if (moveableDimension.value === "y") {
//         size = dropElementSize.height;
//       } else {
//         size = dropElementSize.width;
//       }
//     }

//     const end = start + size;

//     if (start <= data.minStartPos) {
//       data.minStartPos = start;
//     }
//     if (end >= data.maxEndPos) {
//       data.maxEndPos = end;
//     }

//     return {
//       start,
//       size,
//       end
//     };
//   });
//   for (const index in sizes) {
//     data.elementSizes[index] = sizes[index];
//   }
// }

/**
 * Fill the drag "ghost" element that will be
 * dragged instead of the real element.
 */
function prepareDragGhost() {
  const elementsToClone = draggedItemKeys.value
    .map((key) => data.itemRefs[key])
    .filter((el) => el != null);

  const sizes = draggedItemKeys.value.map((el) => {
    const index = findIndexById(el);
    if (index === -1) return { start: 0, size: 0, end: 0 };
    return data.elementSizes[index];
  });
  const minStart = Math.min(...sizes.map((s) => s.start));
  const maxEnd = Math.max(...sizes.map((s) => s.end));
  const size = maxEnd - minStart;

  const dim = moveableDimension.value;
  const listDimensions = getListDimensions();
  const totalHeight = dim === "x" ? listDimensions.height : size;
  const totalWidth = dim === "y" ? listDimensions.width : size;
  data.ghostPosition.x = dim === "x" ? minStart : 0;
  data.ghostPosition.y = dim === "y" ? minStart : 0;

  const dragGhostRef = dragGhostElement.value!;
  const elementToClone = document.createElement("div");
  elementToClone.style.position = "relative";
  elementToClone.style.width = totalWidth + "px";
  elementToClone.style.height = totalHeight + "px";
  dragGhostRef.innerHTML = "";
  for (let i = 0; i < elementsToClone.length; i++) {
    cloneHTMLInto(elementsToClone[i], dragGhostRef);
  }
  dragGhostRef.style.width = totalWidth + "px";
  dragGhostRef.style.height = totalHeight + "px";

  if (group?.id != null) {
    dragGhostRef.dataset.dragGroupId = group?.id;
  } else {
    dragGhostRef.dataset.dragGroupId = undefined;
  }
  dragGhostRef.dataset.sourceListId = id;
}

/**
 * Find the new order-index of an item when dragged by a distance
 * @param currentIndex
 * @param positionStart
 */

function cloneHTMLInto(sourceElement: HTMLElement, targetElement: HTMLElement) {
  targetElement.innerHTML += sourceElement.outerHTML;

  const inputs = sourceElement.querySelectorAll(":is(input, textarea)");
  inputs.forEach((input) => {
    if (input.id) {
      const inputElement = input as HTMLInputElement;
      const clonedInput = targetElement.querySelector(
        `#${input.id}`
      ) as HTMLInputElement;
      if (clonedInput != null) {
        clonedInput.value = inputElement.value;
      } else {
        console.warn("clonedInput is null");
      }
    }
  });
}

// =======================================
// UTILS

/**
 * Compare the order of two keys based on their index in the list.
 * @param a The first key to compare.
 * @param b The second key to compare.
 */
function sortKeysByIndex(a: K | symbol, b: K | symbol) {
  const indexA = findIndexById(a);
  const indexB = findIndexById(b);
  return indexA - indexB;
}

function filterKeyByIndex(key: K | symbol) {
  const index = findIndexById(key);
  return index !== -1;
}

function isBeingDragged(key: K | symbol) {
  return (data.dragging && draggedItemKeys.value.includes(key as K)) || droppingItemKeys.value.includes(key);
}
</script>

<style lang="scss">
.sortable-list {
  position: relative;

  &.-drop-hovering {
    background-color: var(--color-action-light);
    // box-shadow: 0 0 0 5px var(--color-action-light);
    border-radius: 4px;
    outline: 2px solid var(--color-action);
    outline-offset: -2px;
  }
  &.-drop-hovering &__entry {
    pointer-events: none;
  }
  &.-drop-hovering &__entry .sortable-list {
    // Still listen for drop events in nested lists
    pointer-events: auto;
  }
  .is-beeing-dragged > .sortable-list {
    pointer-events: none !important;
  }

  &__entry {
    transition: transform 125ms;
    &.is-beeing-dragged {
      transition: none !important;
      pointer-events: none;
      & > * {
        opacity: 0;
      }
      outline: 2px dashed rgba(var(--tint-contrast), 0.1);
      background-color: rgba(var(--tint-contrast), 0.05);
      outline-offset: -2px;
      pointer-events: none;
      border-radius: 4px;
      overflow: hidden;
    }
    &.-drag-placeholder {
      &::before {
        content: "";
        display: block;
        width: 90%;
        height: 90%;
        outline: 2px dashed rgba(var(--tint-contrast), 0.1);
        background-color: rgba(var(--tint-contrast), 0.05);
        outline-offset: -2px;
        border-radius: 4px;
        box-sizing: border-box;
        pointer-events: none;
      }
    }
    &.-outside {
      height: 0 !important;
      width: 0 !important;
    }
  }
  &__drag-ghost {
    pointer-events: none;
    position: absolute !important;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    gap: var(--gap, 0.5rem);
    z-index: 2;
    & > * {
      pointer-events: none;
      transform: scale3d(1.025, 1.025, 1);
      transition: transform 500ms ease-out;
    }

    &-leave-active {
      transition: all 200ms ease;

      & > * {
        transform: scale3d(1, 1, 1);
      }
    }
  }

  &__gap-helper {
    position: absolute;
    top: 0;
    height: var(--gap, 0.5rem);
    width: var(--gap, 0.5rem);
    pointer-events: none;
    opacity: 0;
  }
}
</style>
